import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutGrid, Calendar, Home, Plus, LogOut, Sparkles, Wallet, Inbox, HeartPulse, Scale } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useData } from "@/lib/store";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const LS_ORG = "aevum_active_org_v1";

function useActiveOrg() {
  const [org, setOrg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/orgs").then((r) => {
      if (cancelled) return;
      const activeId = localStorage.getItem(LS_ORG);
      setOrg(r.data.find((o) => o.id === activeId) || r.data[0] || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return org;
}

const VIEWS_PERSONAL = [
  { to: "/",          label: "Today",     icon: Home,        testId: "nav-today" },
  { to: "/board",     label: "Workspace", icon: LayoutGrid,  testId: "nav-workspace",
    matches: (p) => p === "/board" || p === "/grid" || p === "/timeline" },
  { to: "/money",     label: "Money",     icon: Wallet,      testId: "nav-money" },
  { to: "/health",    label: "Health",    icon: HeartPulse,  testId: "nav-health" },
  { to: "/inbox",     label: "Inbox",     icon: Inbox,       testId: "nav-inbox" },
];
const VIEWS_TEAM = [
  { to: "/",                   label: "Today",     icon: Home,        testId: "nav-today" },
  { to: "/board",              label: "Workspace", icon: LayoutGrid,  testId: "nav-workspace",
    matches: (p) => p === "/board" || p === "/grid" || p === "/timeline" },
  { to: "/money",              label: "Money",     icon: Wallet,      testId: "nav-money" },
  { to: "/financial-health",   label: "Financial Health", icon: Scale, testId: "nav-financial-health" },
  { to: "/inbox",              label: "Inbox",     icon: Inbox,       testId: "nav-inbox" },
];

export default function Sidebar({ onOpenCapture, onOpenBrain }) {
  const { user, logout } = useAuth();
  const { spaces } = useData();
  const location = useLocation();
  const spaceFilter = new URLSearchParams(location.search).get("space");
  const activeOrg = useActiveOrg();
  const VIEWS = activeOrg && !activeOrg.personal ? VIEWS_TEAM : VIEWS_PERSONAL;

  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 bg-surface flex flex-col border-r border-outline-variant" data-testid="app-sidebar">
      {/* Brand */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-hero bg-primary text-primary-foreground flex items-center justify-center font-medium">Æ</div>
        <div>
          <div className="text-lg font-medium text-surface-on leading-none">Aevum</div>
          <div className="text-xs text-surface-onVariant mt-1">Personal admin OS</div>
        </div>
      </div>

      {/* Quick capture + Ask */}
      <div className="px-4 mt-2 space-y-2">
        <button
          data-testid="sidebar-capture-btn"
          onClick={onOpenCapture}
          className="w-full inline-flex items-center gap-2 rounded-full bg-primary-container text-primary-onContainer font-medium px-4 py-3 hover:brightness-95 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md"
        >
          <Plus className="w-4 h-4" />
          Quick capture
        </button>
        <button
          data-testid="sidebar-ask-btn"
          onClick={onOpenBrain}
          className="w-full inline-flex items-center gap-2 rounded-full bg-surface-container hover:bg-primary/8 text-surface-on font-medium px-4 py-3 active:scale-[0.98] transition-[transform,background-color] duration-200 ease-md"
        >
          <Sparkles className="w-4 h-4 text-primary" />
          Ask the Brain
        </button>
      </div>

      {/* Views nav */}
      <nav className="px-3 mt-6 space-y-1">
        <div className="px-3 text-[11px] tracking-widest uppercase font-medium text-surface-onVariant mb-2">Views</div>
        {VIEWS.map(({ to, label, icon: Icon, testId, matches }) => (
          <NavLink
            key={to} to={to} end={to === "/"}
            data-testid={testId}
            className={({ isActive }) => {
              const active = matches ? matches(location.pathname) : isActive;
              return cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ease-md",
                active ? "bg-secondary-container text-secondary-onContainer" : "text-surface-on hover:bg-primary/8"
              );
            }}
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Spaces */}
      <div className="px-3 mt-6 flex-1 overflow-y-auto scrollbar-thin">
        <div className="px-3 flex items-center justify-between mb-2">
          <span className="text-[11px] tracking-widest uppercase font-medium text-surface-onVariant">Spaces</span>
        </div>
        <div className="space-y-1">
          <NavLink
            to="/"
            data-testid="space-all"
            className={cn(
              "flex items-center justify-between gap-3 px-3 py-2 rounded-full text-sm transition-colors duration-200 ease-md",
              !spaceFilter ? "bg-primary/10 text-surface-on" : "text-surface-on hover:bg-primary/8"
            )}
          >
            <span className="flex items-center gap-3"><span className="w-2.5 h-2.5 rounded-full bg-surface-onVariant" /> All spaces</span>
          </NavLink>
          {spaces.map((s) => (
            <NavLink
              key={s.id}
              to={`/?space=${s.id}`}
              data-testid={`space-${s.name.toLowerCase().replace(/\s+/g,'-')}`}
              className={cn(
                "flex items-center justify-between gap-3 px-3 py-2 rounded-full text-sm transition-colors duration-200 ease-md",
                spaceFilter === s.id ? "bg-primary/10 text-surface-on" : "text-surface-on hover:bg-primary/8"
              )}
            >
              <span className="flex items-center gap-3 truncate">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                <span className="truncate">{s.name}</span>
              </span>
              <span className="text-xs text-surface-onVariant shrink-0">{s.open_count ?? 0}</span>
            </NavLink>
          ))}
        </div>
      </div>

      {/* User */}
      <div className="p-4 border-t border-outline-variant">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-tertiary-container text-tertiary-onContainer flex items-center justify-center font-medium text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-surface-on truncate">{user?.name}</div>
            <div className="text-xs text-surface-onVariant truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-full hover:bg-primary/8 text-surface-onVariant transition-colors duration-200 ease-md"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
